/* Inspired by https://usehooks.com/useLocalStorage/ */
import { useEffect, useRef, useState } from "react";

import { compact, isFunction } from "lodash";

import log from "@readai/common/logger";
import { getStorage } from "@readai/common/storage";

type StorageScope = "session" | "local";

interface PartialUser {
  id: string;
}

interface UseLocalStorageOptions {
  enabled?: boolean;
  scope?: StorageScope;
  migrateKeys?: boolean;
  userDependent?: boolean;
  envDependent?: boolean;
  user: PartialUser | null;
}

// unique key for user and environment
// ex: actionSpecificKey_10_staging
const getUniqueKey = (
  key: string,
  user: PartialUser | null,
  {
    uniqueForUser,
    uniqueForEnv,
  }: { uniqueForUser: boolean; uniqueForEnv: boolean },
) => {
  const env = uniqueForEnv
    ? (import.meta.env.VITE_APP_STAGE ?? "development")
    : "";
  const userKey = uniqueForUser ? (user?.id ?? "unknown") : "";
  return compact([key, userKey, env]).join("_");
};

const migrateKey = (
  key: string,
  uniqueKey: string,
  scope: StorageScope,
  enabled = true,
) => {
  if (typeof window === "undefined" || !enabled) return;

  const storage = getStorage(scope);

  const uniqueKeyValue = storage.getItem(uniqueKey);

  // if the unique user/environment version of the key already exists,
  // that means it's already been migrated, so ignore!
  if (uniqueKeyValue) return;

  const keyValue = storage.getItem(key);

  if (keyValue) {
    storage.setItem(uniqueKey, keyValue);
    storage.removeItem(key);
  }
};

const getInitialValue = <T,>(
  key: string,
  initialValue: T,
  scope: StorageScope,
) => {
  if (typeof window === "undefined") {
    return initialValue;
  }

  try {
    // Get from local storage by key
    const item = getStorage(scope).getItem(key);
    // Parse stored json or if none return initialValue
    return item ? JSON.parse(item) : initialValue;
  } catch {
    return initialValue;
  }
};

/**
 * WARNING: DO NOT EXPORT/USE THIS HOOK BY ITSELF!!
 *
 * Doing so will cause values to be persisted irrespective of user or
 * environment. If you need to save a local storage value that cannot
 * depend on the user, set `userDependent: false` in the options when
 * calling the useLocalStorage hook. Likewise, if you want a value
 * that persists across environments, set `envDependent: false`.
 * */
const useAgnosticLocalStorage = <T,>(
  key: string,
  initialValue: T,
  scope: StorageScope = "local",
  enabled = true,
) => {
  const lastEnabled = useRef(enabled);

  const [storedValue, setStoredValue] = useState<T>(
    getInitialValue(key, initialValue, scope),
  );

  const handleSetStoredValue = (value: T | ((state: T) => T)) => {
    const newValue = isFunction(value) ? value(storedValue) : value;
    setStoredValue(newValue);

    if (!enabled) return;

    try {
      getStorage(scope).setItem(key, JSON.stringify(newValue));
    } catch (error) {
      log.debug(error);
    }
  };

  const clearValue = () => {
    try {
      if (typeof window !== "undefined") {
        getStorage(scope).removeItem(key);
      }
    } catch (error) {
      log.debug(error);
    }
  };

  useEffect(() => {
    if (!lastEnabled.current && enabled) {
      setStoredValue(getInitialValue(key, initialValue, scope));
    }

    lastEnabled.current = enabled;
  }, [enabled, lastEnabled.current]);

  return [storedValue, handleSetStoredValue, clearValue] as const;
};

const useLocalStorage = <T,>(
  key: string,
  initialValue: T,
  opts: UseLocalStorageOptions,
) => {
  const user = opts.user;

  const uniqueKey = getUniqueKey(key, user, {
    uniqueForUser: opts?.userDependent ?? true,
    uniqueForEnv: opts?.envDependent ?? true,
  });

  const scope = opts?.scope ?? "local";

  if (opts?.migrateKeys) {
    migrateKey(key, uniqueKey, scope, opts?.enabled);
  }

  return useAgnosticLocalStorage(uniqueKey, initialValue, scope, opts?.enabled);
};

export const setLocalStorageValue = <T,>(
  key: string,
  value: T,
  opts: UseLocalStorageOptions,
) => {
  const scope = opts?.scope ?? "local";

  const uniqueKey = getUniqueKey(key, opts.user, {
    uniqueForUser: opts?.userDependent ?? true,
    uniqueForEnv: opts?.envDependent ?? true,
  });

  getStorage(scope).setItem(uniqueKey, JSON.stringify(value));
};

export default useLocalStorage;
